/**
 * Ted #32c / epic E3.2 (#1158) — the desk's "No-show" call, as the throwing
 * helper every other lifecycle action is (`checkInBooking`, `cancelBooking`):
 * resolves the updated booking, throws an `ApiError` carrying the server's own
 * sentence ("This dog is already checked in…") on a refusal.
 *
 * It rides `adminBookingApi.markNoShow` — the V2 stub in lib/api.ts, now wired
 * to the mounted route `POST /api/bookings/admin/:id/no-show` — so that file's
 * diff stays one function. The `api.*` verbs never throw (they return
 * `{ error }`), so the translation to a throw lives here, once.
 */
import { adminBookingApi, ApiError, type AdminBooking } from '../../lib/api.js';

/** The reason the desk's one-tap path prefills — the reason is required, so it starts answered. */
export const DEFAULT_NO_SHOW_REASON = 'Did not arrive';

export async function markBookingNoShow(
  apiUrl: string,
  bookingId: string,
  reason: string,
): Promise<AdminBooking> {
  const res = await adminBookingApi.markNoShow(apiUrl, bookingId, reason);
  if (res.error !== undefined || !res.data) {
    // `ApiResponse` does not carry the HTTP status, only the sentence and the
    // machine `code` (what a caller branches on). A retryable failure (the
    // wire, a cold start) reads 503; an answered refusal reads 409.
    throw new ApiError(res.isRetryable ? 503 : 409, res.error ?? 'Could not mark the reservation a no-show.', res.code);
  }
  return res.data.booking;
}
